<template>
  <div class="bg-white rounded-lg shadow-md p-4">    
    <!-- Loading state -->
    <div v-if="loading" class="flex justify-center items-center py-4">
      <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      <span class="ml-2 text-gray-700 text-sm">Loading...</span>
    </div>
    
    <!-- Configuration content -->
    <div v-else>
      <!-- Template Reference File Section -->
      <div class="mb-8">
        <h4 class="text-lg font-bold mb-3 text-gray-900">Reference Template File</h4>
        
        <div class="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
          <p class="text-sm text-gray-600 mb-2">
            Select an XLSX file to use as the reference "empty template" for template generation.
          </p>
          
          <!-- Current template display -->
          <div v-if="loading" class="flex items-center py-2">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <span class="text-gray-700 text-sm">Loading template information...</span>
          </div>
          
          <div v-else-if="referenceTemplateId" class="flex items-center justify-between bg-white p-3 rounded border border-gray-200 mb-3">
            <div class="flex items-center">
              <svg class="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span class="text-gray-800">{{ referenceTemplateName }}</span>
            </div>
            <button
              @click="removeReferenceTemplate"
              class="text-red-500 hover:text-red-700 focus:outline-none"
              title="Remove reference template"
              :disabled="loading || updatingTemplate"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div v-else class="text-gray-500 italic mb-3">
            No reference template selected
          </div>
          
          <!-- Template selection dropdown -->
          <div class="relative">
            <div v-if="loadingTemplates" class="flex items-center py-2">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              <span class="text-gray-700 text-sm">Loading available templates...</span>
            </div>
            
            <div v-else-if="xlsxTemplates.length === 0" class="text-gray-500 italic">
              No XLSX templates available
            </div>
            
            <div v-else class="flex flex-wrap gap-2 items-center">
              <div class="flex-1 min-w-[200px]">
                <select
                  v-model="selectedTemplateId"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  :disabled="loading || updatingTemplate"
                >
                  <option value="" disabled>Select a template</option>
                  <option
                    v-for="template in xlsxTemplates"
                    :key="template.id"
                    :value="template.id"
                  >
                    {{ template.name }}
                  </option>
                </select>
              </div>
              
              <button
                @click="setReferenceTemplate"
                :disabled="!selectedTemplateId || loading || updatingTemplate"
                class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="updatingTemplate">Setting...</span>
                <span v-else>Set as Reference</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Board Meeting Attendees Section -->
      <div>
        <h4 class="text-lg font-bold mb-3 text-gray-900">Board Meeting Attendees</h4>
        
        <!-- Current attendees list -->
        <div class="mb-4">
          <div v-if="boardMeetingAttendees.length === 0" class="text-gray-500 italic">
            No attendees configured
          </div>
          <ul v-else class="flex flex-wrap gap-2">
            <li
              v-for="(email, index) in boardMeetingAttendees"
              :key="index"
              class="flex items-center bg-gray-50 py-2 px-3 rounded border border-gray-200"
            >
              <span class="text-gray-800 mr-2">{{ email }}</span>
              <button
                @click="removeAttendee(index)"
                class="text-red-500 hover:text-red-700 focus:outline-none"
                title="Remove attendee"
                :disabled="loading"
              >
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </li>
          </ul>
        </div>
        
        <!-- Add new attendee form and navigation -->
        <div class="mb-2">
          <!-- Error message (inline with the form) -->
          <div v-if="error" class="bg-red-50 border-l-4 border-red-500 p-3 mb-3 text-sm">
            <p class="text-red-700">{{ errorMessage }}</p>
          </div>
          
          <div class="flex flex-wrap gap-2 items-center">
            <div class="flex-1 min-w-[200px] relative">
              <input
                v-model="newAttendeeEmail"
                type="email"
                placeholder="Enter email address"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                :disabled="loading || addingAttendee"
                @keyup.enter="newAttendeeEmail && addAttendee()"
                @input="handleEmailInput(); error = false; errorMessage = '';"
                @focus="handleFocus"
                @blur="handleBlur"
              />
              
              <!-- Email autocomplete dropdown -->
              <div
                v-if="showEmailDropdown"
                class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
              >
                <div v-if="isLoadingEmails" class="p-2 text-center text-gray-500">
                  <div class="animate-spin inline-block rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  Loading...
                </div>
                <div v-else-if="displayEmails.length === 0" class="p-4 text-center text-gray-500">
                  No matching emails found
                </div>
                <ul v-else>
                  <li
                    v-for="(email, index) in displayEmails"
                    :key="index"
                    @click="selectEmail(email)"
                    class="px-4 py-2 hover:bg-gray-100 cursor-pointer text-gray-700"
                  >
                    {{ email }}
                  </li>
                </ul>
              </div>
            </div>
            
            <button
              @click="addAttendee"
              :disabled="!newAttendeeEmail || loading || addingAttendee"
              class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="addingAttendee">Adding...</span>
              <span v-else>Add</span>
            </button>
            
            <router-link
              to="/administration"
              class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Return to Administration
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { systemConfigService } from '@/features/system-config';
import { supabase } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';

// Define a simple interface for the file objects we need
interface FileTemplate {
  id: string;
  name: string;
  type: string;
  url: string;
}

// State
const loading = ref(true);
const error = ref(false);
const errorMessage = ref('');
const configData = ref<any[]>([]);
const newAttendeeEmail = ref('');
const addingAttendee = ref(false);
const profileEmails = ref<string[]>([]);
const filteredEmails = ref<string[]>([]);
const showEmailDropdown = ref(false);
const isLoadingEmails = ref(false);

// Template reference state
const xlsxTemplates = ref<FileTemplate[]>([]);
const loadingTemplates = ref(false);
const selectedTemplateId = ref('');
const updatingTemplate = ref(false);

// Computed properties
const boardMeetingAttendees = computed(() => {
  const config = configData.value.find(item => item.property === 'board_meeting_attendees');
  return config ? config.value : [];
});

// Reference template computed properties
const referenceTemplateId = computed(() => {
  const config = configData.value.find(item => item.property === 'reference_empty_template');
  return config ? config.value : null;
});

const referenceTemplateName = computed(() => {
  if (!referenceTemplateId.value) return '';
  
  const template = xlsxTemplates.value.find(t => t.id === referenceTemplateId.value);
  return template ? template.name : 'Unknown template';
});

// Computed property for display emails
const displayEmails = computed(() => {
  if (!newAttendeeEmail.value.trim()) {
    // Show all emails when input is empty
    return profileEmails.value;
  }
  
  // Show filtered emails when user is typing
  return filteredEmails.value;
});

// Methods
const fetchConfig = async () => {
  loading.value = true;
  error.value = false;
  errorMessage.value = '';
  
  try {
    // Make a direct fetch to see the raw response
    const rawResponse = await fetch((process.env.VUE_APP_FISSION_URL || 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/') + 'system-config');
    const rawData = await rawResponse.json();
    console.log('Raw API response:', rawData);
    
    const response = await systemConfigService.getAllConfig();
    console.log('System config service response:', response);
    
    // Also try to fetch directly from Supabase
    const { data: supabaseData, error: supabaseError } = await supabase
      .from('system_configuration')
      .select('*');
    
    if (supabaseError) {
      console.error('Error fetching from Supabase:', supabaseError);
    } else {
      console.log('Direct Supabase fetch result:', supabaseData);
      
      // Check if reference_empty_template exists in Supabase data
      const referenceTemplateInSupabase = supabaseData?.some(item =>
        item.property === 'reference_empty_template' && item.value !== null
      );
      
      console.log('Reference template in Supabase:', referenceTemplateInSupabase);
      
      // If it exists in Supabase but not in API response, add it to configData
      if (referenceTemplateInSupabase) {
        const templateConfig = supabaseData.find(item => item.property === 'reference_empty_template');
        if (templateConfig && templateConfig.value) {
          console.log('Adding reference template from Supabase:', templateConfig);
          
          // Add to configData if not already there
          if (!response.some(item => item.property === 'reference_empty_template')) {
            response.push(templateConfig);
          }
        }
      }
    }
    
    configData.value = response;
    
    // Log the configuration data for debugging
    logger.info('Fetched configuration data', LogCategory.GENERAL, {
      configCount: configData.value.length,
      configData: configData.value
    });
    
    // Check if reference_empty_template exists
    const hasReferenceTemplate = configData.value.some(item => item.property === 'reference_empty_template');
    console.log('Has reference template property:', hasReferenceTemplate);
  } catch (err: any) {
    error.value = true;
    errorMessage.value = err.message || 'Failed to load configuration';
    console.error('Error fetching configuration:', err);
  } finally {
    loading.value = false;
  }
};

// Validate email format
const isValidEmail = (email: string): boolean => {
  // Basic validation to check if email contains @ symbol
  if (!email.includes('@')) {
    return false;
  }
  
  // More comprehensive validation using regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const addAttendee = async () => {
  if (!newAttendeeEmail.value || addingAttendee.value) return;
  
  // Validate email format
  if (!isValidEmail(newAttendeeEmail.value)) {
    error.value = true;
    errorMessage.value = 'Invalid email format. Please enter a valid email address.';
    return;
  }
  
  addingAttendee.value = true;
  error.value = false;
  errorMessage.value = '';
  
  try {
    // Save the current configuration data
    const currentConfig = [...configData.value];
    
    // Create a new array with the added email
    const updatedAttendees = [...boardMeetingAttendees.value, newAttendeeEmail.value];
    
    // Prepare the configurations to update
    const configs = [
      { property: 'board_meeting_attendees', value: updatedAttendees }
    ];
    
    // If reference_empty_template exists, include it in the update
    const referenceTemplateConfig = currentConfig.find(item => item.property === 'reference_empty_template');
    if (referenceTemplateConfig) {
      configs.push({ property: 'reference_empty_template', value: referenceTemplateConfig.value });
    }
    
    // Update both configurations at once
    await systemConfigService.updateMultipleConfigs(configs);
    
    // Refresh the configuration data
    await fetchConfig();
    
    // Clear the input
    newAttendeeEmail.value = '';
  } catch (err: any) {
    error.value = true;
    errorMessage.value = err.message || 'Failed to add attendee';
    console.error('Error adding attendee:', err);
  } finally {
    addingAttendee.value = false;
  }
};

const removeAttendee = async (index: number) => {
  try {
    // Save the current configuration data
    const currentConfig = [...configData.value];
    
    // Create a new array without the removed email
    const updatedAttendees = boardMeetingAttendees.value.filter((_, i) => i !== index);
    
    // Prepare the configurations to update
    const configs = [
      { property: 'board_meeting_attendees', value: updatedAttendees }
    ];
    
    // If reference_empty_template exists, include it in the update
    const referenceTemplateConfig = currentConfig.find(item => item.property === 'reference_empty_template');
    if (referenceTemplateConfig) {
      configs.push({ property: 'reference_empty_template', value: referenceTemplateConfig.value });
    }
    
    // Update both configurations at once
    await systemConfigService.updateMultipleConfigs(configs);
    
    // Refresh the configuration data
    await fetchConfig();
  } catch (err: any) {
    error.value = true;
    errorMessage.value = err.message || 'Failed to remove attendee';
    console.error('Error removing attendee:', err);
  }
};

// Fetch emails from profiles table
const fetchProfileEmails = async () => {
  isLoadingEmails.value = true;
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('email')
      .not('email', 'is', null);
    
    if (error) {
      throw error;
    }
    
    // Extract emails and filter out nulls
    profileEmails.value = data
      .map(profile => profile.email)
      .filter(email => email) as string[];
    
    logger.info('Fetched profile emails for autocomplete', LogCategory.GENERAL, {
      count: profileEmails.value.length
    });
  } catch (err: any) {
    logger.error('Error fetching profile emails', LogCategory.GENERAL, { error: err });
    console.error('Error fetching profile emails:', err);
  } finally {
    isLoadingEmails.value = false;
  }
};

// Handle focus on the email field
const handleFocus = async () => {
  showEmailDropdown.value = true;
  
  // If we haven't loaded emails yet, load them now
  if (profileEmails.value.length === 0) {
    await fetchProfileEmails();
  }
};

// Handle blur on the email field
const handleBlur = () => {
  // Use window.setTimeout instead of setTimeout directly
  window.setTimeout(() => {
    showEmailDropdown.value = false;
  }, 200);
};

// Handle input in the email field
const handleEmailInput = () => {
  // Filter emails based on input
  const input = newAttendeeEmail.value.toLowerCase().trim();
  
  if (input === '') {
    // When input is empty, we'll show all emails (handled by displayEmails computed property)
    filteredEmails.value = [];
    return;
  }
  
  // Filter emails that START with the input text
  filteredEmails.value = profileEmails.value
    .filter(email => email.toLowerCase().startsWith(input));
};

// Select an email from the dropdown
const selectEmail = (email: string) => {
  newAttendeeEmail.value = email;
  showEmailDropdown.value = false;
};

// Template methods
const fetchTemplates = async () => {
  loadingTemplates.value = true;
  try {
    const { data, error } = await supabase
      .from('files')
      .select('id, name, type, url')
      .eq('type', 'xlsx');
    
    if (error) {
      throw error;
    }
    
    xlsxTemplates.value = data as FileTemplate[] || [];
    logger.info('Fetched XLSX templates', LogCategory.GENERAL, {
      count: xlsxTemplates.value.length
    });
  } catch (err: any) {
    logger.error('Error fetching XLSX templates', LogCategory.GENERAL, { error: err });
    console.error('Error fetching XLSX templates:', err);
  } finally {
    loadingTemplates.value = false;
  }
};

const setReferenceTemplate = async () => {
  if (!selectedTemplateId.value || updatingTemplate.value) return;
  
  updatingTemplate.value = true;
  error.value = false;
  errorMessage.value = '';
  
  try {
    // We don't need to save the current configuration data anymore
    
    console.log('Attempting to save reference_empty_template directly to Supabase');
    
    // Try to directly insert into the system_configuration table
    const { data: existingConfig, error: fetchError } = await supabase
      .from('system_configuration')
      .select('*')
      .eq('property', 'reference_empty_template')
      .single();
    
    let result;
    
    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error checking for existing config:', fetchError);
      throw fetchError;
    }
    
    if (existingConfig) {
      // Update existing record
      console.log('Updating existing record:', existingConfig);
      const { data, error } = await supabase
        .from('system_configuration')
        .update({ value: selectedTemplateId.value })
        .eq('property', 'reference_empty_template')
        .select();
      
      if (error) {
        console.error('Error updating config:', error);
        throw error;
      }
      
      result = data;
    } else {
      // Insert new record
      console.log('Inserting new record for reference_empty_template');
      const { data, error } = await supabase
        .from('system_configuration')
        .insert({
          property: 'reference_empty_template',
          value: selectedTemplateId.value
        })
        .select();
      
      if (error) {
        console.error('Error inserting config:', error);
        throw error;
      }
      
      result = data;
    }
    
    console.log('Direct Supabase operation result:', result);
    
    // Also try the API method as a backup
    try {
      const updateResult = await systemConfigService.updateConfig('reference_empty_template', selectedTemplateId.value);
      console.log('API update result:', updateResult);
    } catch (apiError) {
      console.error('API update failed, but direct DB update succeeded:', apiError);
    }
    
    // Refresh the configuration data
    await fetchConfig();
    
    // Clear the selection
    selectedTemplateId.value = '';
    
    logger.info('Reference template set successfully', LogCategory.GENERAL, {
      templateId: selectedTemplateId.value
    });
  } catch (err: any) {
    error.value = true;
    errorMessage.value = err.message || 'Failed to set reference template';
    console.error('Error setting reference template:', err);
  } finally {
    updatingTemplate.value = false;
  }
};

const removeReferenceTemplate = async () => {
  updatingTemplate.value = true;
  error.value = false;
  errorMessage.value = '';
  
  try {
    // We don't need to save the current configuration data anymore
    
    console.log('Attempting to remove reference_empty_template directly from Supabase');
    
    // Try to directly update the system_configuration table
    const { data, error } = await supabase
      .from('system_configuration')
      .update({ value: null })
      .eq('property', 'reference_empty_template')
      .select();
    
    if (error) {
      console.error('Error removing config:', error);
      throw error;
    }
    
    console.log('Direct Supabase operation result:', data);
    
    // Also try the API method as a backup
    try {
      const updateResult = await systemConfigService.updateConfig('reference_empty_template', null);
      console.log('API update result:', updateResult);
    } catch (apiError) {
      console.error('API update failed, but direct DB update succeeded:', apiError);
    }
    
    // Refresh the configuration data
    await fetchConfig();
    
    logger.info('Reference template removed successfully', LogCategory.GENERAL);
  } catch (err: any) {
    error.value = true;
    errorMessage.value = err.message || 'Failed to remove reference template';
    console.error('Error removing reference template:', err);
  } finally {
    updatingTemplate.value = false;
  }
};

// Fetch configuration data when component is mounted
onMounted(() => {
  fetchConfig();
  fetchProfileEmails();
  fetchTemplates();
});
</script>
